import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

export const LICENSE_TIERS = {
  BASE: "base",
  PLUS: "plus",
  PRO: "pro",
};

export const BASE_FEATURES = {
  webapp_access: true,
  pingflo_ai: true, // Pingflo, Pingwi, Pingpro, Pingmynd
  smart_profiling: true,
  king_of_the_table: true,
  video_review: true,
  auto_eval: true,
  gdpr_media_consent: true,
  video_retention_days: 30,
  /** Distinct training sessions with eval-video upload per calendar month. -1 = unlimited. */
  monthly_eval_quota: -1,
  remote_camera_rig: false,
  pose_frame_angles: false,
  stroke_isolation: false,
  stroke_overlay_export: false,
  technique_labeling: false,
  progress_snapshots: false,
  slow_mo_reel_export: false,
  pose_stroke_count: false,
  max_rally_detection: false,
  ball_placement_analytics: false,
  ball_error_tracking: false,
  service_analytics: false,
  mobile_app_120fps: false,
  hr_biofeedback: false,
  hr_effort_metrics: false,
  mobile_app_hr_pairing: false,
};

export const PLUS_FEATURES = {
  ...BASE_FEATURES,
  video_retention_days: 60,
  monthly_eval_quota: -1,
  remote_camera_rig: true,
  pose_frame_angles: true,
  stroke_isolation: true,
  stroke_overlay_export: true,
  technique_labeling: true,
  progress_snapshots: true,
  slow_mo_reel_export: true,
};

export const PRO_FEATURES = {
  ...PLUS_FEATURES,
  video_retention_days: 365,
  monthly_eval_quota: -1,
  pose_stroke_count: true,
  max_rally_detection: true,
  ball_placement_analytics: true,
  ball_error_tracking: true,
  service_analytics: true,
  mobile_app_120fps: true,
};

export const HR_ADDON_FEATURES = {
  hr_biofeedback: true,
  hr_effort_metrics: true,
  mobile_app_hr_pairing: true,
};

export function normalizeTier(tier) {
  const t = String(tier || "").trim().toLowerCase();
  if (t === LICENSE_TIERS.PRO) return LICENSE_TIERS.PRO;
  if (t === LICENSE_TIERS.PLUS) return LICENSE_TIERS.PLUS;
  return LICENSE_TIERS.BASE;
}

export function resolveFeatures(tier, addonHr = false, overrides = null) {
  const norm = normalizeTier(tier);
  let baseConfig = BASE_FEATURES;
  if (norm === LICENSE_TIERS.PRO) baseConfig = PRO_FEATURES;
  else if (norm === LICENSE_TIERS.PLUS) baseConfig = PLUS_FEATURES;

  const merged = { ...baseConfig };
  if (addonHr) {
    Object.assign(merged, HR_ADDON_FEATURES);
  }
  if (overrides && typeof overrides === "object") {
    Object.assign(merged, overrides);
  }
  return merged;
}

export function allowedRecordingFps(features = {}) {
  if (features?.mobile_app_120fps) return [30, 60, 120];
  return [30, 60];
}

const ClubLicenseContext = createContext({
  tier: LICENSE_TIERS.BASE,
  addonHr: false,
  features: BASE_FEATURES,
  hasFeature: () => false,
  validUntil: null,
  isExpired: false,
  retentionDays: 30,
  monthlyEvalQuota: 10,
  recordingFpsOptions: [30, 60],
  coachFeatures: [],
  playerFeatures: [],
  setLicense: () => {},
});

export const ClubLicenseProvider = ({ children, initialLicense }) => {
  const [licenseState, setLicenseState] = useState(() => {
    if (initialLicense) return initialLicense;
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const u = JSON.parse(storedUser);
        if (u.club_license) return u.club_license;
      }
      const storedBrand = localStorage.getItem("brandConfig");
      if (storedBrand) {
        const b = JSON.parse(storedBrand);
        if (b.club_license) return b.club_license;
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  });

  // Keep in sync with initialLicense prop if provided or changed
  useEffect(() => {
    if (initialLicense) {
      setLicenseState(initialLicense);
    }
  }, [initialLicense]);

  const value = useMemo(() => {
    const tier = normalizeTier(licenseState?.tier || licenseState?.license_tier);
    const addonHr = Boolean(licenseState?.addon_hr ?? licenseState?.addonHr);
    const overrides = licenseState?.overrides || licenseState?.license_features_override || null;
    const features = resolveFeatures(tier, addonHr, overrides);
    const validUntil = licenseState?.valid_until || licenseState?.license_valid_until || null;
    const isExpired = validUntil ? new Date(validUntil).getTime() < Date.now() : false;

    const hasFeature = (key) => Boolean(features[key]);

    return {
      tier,
      addonHr,
      features,
      hasFeature,
      validUntil,
      isExpired,
      retentionDays: features.video_retention_days || 30,
      monthlyEvalQuota: features.monthly_eval_quota ?? -1,
      recordingFpsOptions: allowedRecordingFps(features),
      coachFeatures: licenseState?.coach_features || [],
      playerFeatures: licenseState?.player_features || [],
      setLicense: setLicenseState,
    };
  }, [licenseState]);

  return (
    <ClubLicenseContext.Provider value={value}>
      {children}
    </ClubLicenseContext.Provider>
  );
};

export const useClubLicense = () => useContext(ClubLicenseContext);

export default ClubLicenseContext;
